import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useStaffRole } from "../lib/admin";
import { canManage, loadPeople, managedOrgs, orgApi, type Contact, type People } from "../lib/org";
import { Drawer, Field, Notice } from "./admin/ui";
import RecipientPicker from "../ui/RecipientPicker";
import CompanyBadge, { logoUrl, refreshCompanyBadge } from "../ui/CompanyBadge";
import TeamPresence from "../ui/TeamPresence";
import AppFooter from "../ui/AppFooter";
import UserMenu from "../ui/UserMenu";

type Org = {
  id: string; name: string; type: string; logo_path: string | null; updated_at: string | null;
  contact_name: string | null; contact_email: string | null; contact_phone: string | null; address: string | null;
  alert_emails: string[]; reminder_emails: string[]; reminder_days: number[];
};
const ROLE_LABEL: Record<string, string> = { org_owner: "Owner", org_user: "User", viewer: "Viewer", skyward_admin: "Skyward admin", skyward_tech: "Skyward tech" };

/** Company settings — the company owner and Skyward staff can edit; other members see the basics. */
export default function CompanyPage({ session }: { session: Session }) {
  const staff = useStaffRole(session.user.id);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgId, setOrgId] = useState("");
  const [managed, setManaged] = useState<Set<string> | "all" | null>(null);
  const [people, setPeople] = useState<People | null>(null);
  const [form, setForm] = useState<Partial<Org>>({});
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [editContact, setEditContact] = useState<Contact | "new" | null>(null);

  const loadOrgs = useCallback(async () => {
    const { data } = await supabase.from("organisations")
      .select("id, name, type, logo_path, updated_at, contact_name, contact_email, contact_phone, address, alert_emails, reminder_emails, reminder_days")
      .neq("status", "closed").order("name");
    const list = (data ?? []) as Org[];
    setOrgs(list);
    setOrgId((cur) => cur || list.find((o) => o.type === "internal")?.id || list[0]?.id || "");
  }, []);
  useEffect(() => { managedOrgs(session.user.id).then(setManaged); loadOrgs(); }, [session.user.id, loadOrgs]);

  const org = orgs.find((o) => o.id === orgId);
  const editable = canManage(managed, orgId);
  useEffect(() => {
    if (!org) return;
    setForm({ contact_name: org.contact_name, contact_email: org.contact_email, contact_phone: org.contact_phone, address: org.address,
      alert_emails: org.alert_emails ?? [], reminder_emails: org.reminder_emails ?? [] });
    setDays((org.reminder_days ?? [60, 30, 7, 0]).join(", "));
    setPeople(null);
    if (editable) loadPeople(org.id, true).then(setPeople).catch(() => setPeople(null));
  }, [orgId, org?.updated_at, editable]); // eslint-disable-line

  const set = (k: keyof Org) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  /** Fill the main contact from a company user or saved contact. */
  function pickMain(v: string) {
    const [kind, id] = v.split(":");
    const p = kind === "u" ? people?.users.find((u) => u.id === id) : people?.contacts.find((c) => c.id === id);
    if (!p) return;
    setForm({ ...form, contact_name: p.name ?? form.contact_name ?? null, contact_email: p.email ?? null, contact_phone: p.phone ?? form.contact_phone ?? null });
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    setBusy(true); setMsg(null);
    try {
      await orgApi("PATCH", `company/${org.id}`, { ...form, reminder_days: days.split(/[,;\s]+/).filter(Boolean).map(Number) });
      await loadOrgs();
      setMsg({ kind: "ok", text: "Company settings saved." });
    } catch (x: any) { setMsg({ kind: "bad", text: x.message }); } finally { setBusy(false); }
  }

  return (
    <div className="admin company">
      <header className="topbar">
        <img src="/logo.png" alt="Tracking Fiji" className="topbar-logo" />
        <span className="beta">Company</span>
        <div className="topbar-spacer" />
        <CompanyBadge userId={session.user.id} />
        <TeamPresence />
        <Link className="btn-ghost btn-map" to="/map">← Map</Link>
        <Link className="btn-ghost" to="/fleet">Fleet</Link>
        {staff && <Link className="btn-ghost" to="/admin/companies">Admin</Link>}
        <UserMenu email={session.user.email} />
      </header>
      <main className="amain co-main">
        <div className="atoolbar">
          <h1>Company settings</h1>
          {orgs.length > 1 && (
            <select className="asearch" value={orgId} onChange={(e) => { setOrgId(e.target.value); setMsg(null); }}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>
        {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
        {!org ? <div className="rep-empty">Loading…</div> : (
          <>
            {!editable && <Notice kind="warn">Only the company owner (or Skyward) can change these settings.</Notice>}
            <section className="co-card">
              <h2>Logo</h2>
              <LogoEditor org={org} editable={editable} onChanged={async () => { await loadOrgs(); refreshCompanyBadge(); }} onError={(t) => setMsg({ kind: "bad", text: t })} />
            </section>

            <form className="co-card aform" onSubmit={save}>
              <h2 className="wide">Main contact <span className="sub">— used for account and admin emails</span></h2>
              {editable && people && (people.users.length + people.contacts.length) > 0 && (
                <Field label="Fill from a user or contact" wide>
                  <select value="" onChange={(e) => pickMain(e.target.value)}>
                    <option value="">Choose…</option>
                    {people.users.length > 0 && <optgroup label="Users">{people.users.map((u) => <option key={u.id} value={`u:${u.id}`}>{u.name || u.email} — {u.email}</option>)}</optgroup>}
                    {people.contacts.length > 0 && <optgroup label="Contacts">{people.contacts.map((c) => <option key={c.id} value={`c:${c.id}`}>{c.name}{c.role ? ` (${c.role})` : ""}</option>)}</optgroup>}
                  </select>
                </Field>
              )}
              <fieldset disabled={!editable} style={{ display: "contents" }}>
                <Field label="Name"><input value={form.contact_name ?? ""} onChange={set("contact_name")} maxLength={120} /></Field>
                <Field label="Email"><input type="email" value={form.contact_email ?? ""} onChange={set("contact_email")} maxLength={120} /></Field>
                <Field label="Phone"><input value={form.contact_phone ?? ""} onChange={set("contact_phone")} maxLength={40} /></Field>
                <Field label="Address" wide><textarea rows={2} value={form.address ?? ""} onChange={set("address")} maxLength={300} /></Field>
              </fieldset>

              <h2 className="wide">Default recipients</h2>
              <Field label="Alert emails" wide hint="Pre-filled on new alert rules (speeding, zones, SOS …).">
                <RecipientPicker org={org.id} value={form.alert_emails ?? []} onChange={(v) => setForm({ ...form, alert_emails: v })} disabled={!editable} />
              </Field>
              <Field label="Fleet renewal reminders" wide hint="Daily summary around 7am (Fiji) of registrations, inspections, insurance and licences that are due or expired.">
                <RecipientPicker org={org.id} value={form.reminder_emails ?? []} onChange={(v) => setForm({ ...form, reminder_emails: v })} disabled={!editable} />
              </Field>
              <Field label="Remind this many days before expiry" wide hint="e.g. 60, 30, 7, 0 — plus once when it has expired.">
                <input value={days} onChange={(e) => setDays(e.target.value)} disabled={!editable} />
              </Field>
              {editable && <div className="wide co-actions"><button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save settings"}</button></div>}
            </form>

            <section className="co-card">
              <div className="co-head">
                <h2>Contacts</h2>
                <span className="sub">Extra people to choose for alerts and reminders — they don't get a login.</span>
                {editable && <button className="btn-ghost" onClick={() => setEditContact("new")}>+ Add contact</button>}
              </div>
              {!editable ? <p className="sub">Visible to the company owner.</p> : !people ? <p className="sub">Loading…</p> : (
                <div className="atable-wrap">
                  <table className="atable">
                    <thead><tr><th>Name</th><th>Role</th><th>Email</th><th>Phone</th><th /></tr></thead>
                    <tbody>
                      {people.contacts.map((c) => (
                        <tr key={c.id} onClick={() => setEditContact(c)}>
                          <td><b>{c.name}</b></td><td>{c.role ?? "—"}</td><td className="mono">{c.email ?? "—"}</td><td className="mono">{c.phone ?? "—"}</td>
                          <td className="num"><button className="btn-ghost small">Edit</button></td>
                        </tr>
                      ))}
                      {!people.contacts.length && <tr className="empty"><td colSpan={5}>No extra contacts yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {editable && people && (
              <section className="co-card">
                <div className="co-head">
                  <h2>Users</h2>
                  <span className="sub">People who can sign in to Tracking Fiji for this company.</span>
                  {staff && <Link className="btn-ghost" to={`/admin/companies?org=${org.id}`}>Manage in Admin</Link>}
                </div>
                <div className="atable-wrap">
                  <table className="atable static">
                    <thead><tr><th>Name</th><th>Email</th><th>Role</th></tr></thead>
                    <tbody>
                      {people.users.map((u) => <tr key={u.id}><td><b>{u.name ?? "—"}</b></td><td className="mono">{u.email}</td><td>{ROLE_LABEL[u.role ?? ""] ?? u.role}</td></tr>)}
                      {!people.users.length && <tr className="empty"><td colSpan={3}>No users yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
                {!staff && <p className="sub">To add or remove users, contact Skyward Industries.</p>}
              </section>
            )}
          </>
        )}
      </main>
      <AppFooter />
      {editContact && org && (
        <ContactForm org={org.id} contact={editContact === "new" ? null : editContact} onClose={() => setEditContact(null)}
          onSaved={async () => { setEditContact(null); setPeople(await loadPeople(org.id, true)); }} />
      )}
    </div>
  );
}

function LogoEditor({ org, editable, onChanged, onError }: { org: Org; editable: boolean; onChanged: () => Promise<void>; onError: (t: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const url = logoUrl(org.logo_path, org.updated_at);
  async function upload(f: File | undefined) {
    if (!f) return;
    if (f.size > 1024 * 1024) return onError("Logo must be 1 MB or smaller.");
    setBusy(true);
    try {
      const u = await orgApi<{ path: string; token: string }>("POST", `company/${org.id}/logo-url`, { name: f.name, size: f.size, mime: f.type });
      const { error } = await supabase.storage.from("org-logos").uploadToSignedUrl(u.path, u.token, f, { contentType: f.type });
      if (error) throw new Error(error.message);
      await orgApi("POST", `company/${org.id}/logo`, { path: u.path });
      await onChanged();
    } catch (x: any) { onError(x.message); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  }
  async function remove() {
    if (!confirm("Remove the logo? The company name will be shown instead.")) return;
    setBusy(true);
    try { await orgApi("DELETE", `company/${org.id}/logo`); await onChanged(); } catch (x: any) { onError(x.message); } finally { setBusy(false); }
  }
  return (
    <div className="logo-ed">
      <div className="logo-prev">{url ? <img src={url} alt={`${org.name} logo`} /> : <span>{org.name}</span>}</div>
      <div className="logo-side">
        <p className="sub">Shown in the top bar for everyone in {org.name}. PNG, JPG, WebP or SVG, up to 1 MB — a wide logo on a transparent background works best. Without a logo, the company name is shown.</p>
        {editable && (
          <div className="row-inline">
            <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={(e) => upload(e.target.files?.[0])} />
            <button className="btn-ghost" disabled={busy} onClick={() => input.current?.click()}>{busy ? "Uploading…" : url ? "Replace logo" : "Upload logo"}</button>
            {url && <button className="btn-ghost danger" disabled={busy} onClick={remove}>Remove</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function ContactForm({ org, contact, onClose, onSaved }: { org: string; contact: Contact | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: contact?.name ?? "", role: contact?.role ?? "", email: contact?.email ?? "", phone: contact?.phone ?? "", notes: contact?.notes ?? "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  async function save(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      if (contact) await orgApi("PATCH", `contacts/${contact.id}`, f); else await orgApi("POST", "contacts", { ...f, org_id: org });
      onSaved();
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!contact || !confirm(`Delete ${contact.name}?`)) return;
    try { await orgApi("DELETE", `contacts/${contact.id}`); onSaved(); } catch (x: any) { setErr(x.message); }
  }
  return (
    <Drawer title={contact ? contact.name : "Add contact"} onClose={onClose}
      footer={<>
        {contact && <button className="btn-ghost danger" onClick={del} style={{ marginRight: "auto" }}>Delete</button>}
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="contact-form" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
      </>}>
      <form id="contact-form" className="aform" onSubmit={save}>
        <Field label="Name *" wide><input value={f.name} onChange={set("name")} required maxLength={80} /></Field>
        <Field label="Role" wide hint="e.g. Accounts, Fleet manager, Workshop"><input value={f.role} onChange={set("role")} maxLength={60} /></Field>
        <Field label="Email"><input type="email" value={f.email} onChange={set("email")} maxLength={120} /></Field>
        <Field label="Phone"><input value={f.phone} onChange={set("phone")} maxLength={40} /></Field>
        <Field label="Notes" wide><textarea rows={2} value={f.notes} onChange={set("notes")} maxLength={500} /></Field>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}
